const CREDENTIAL_REFRESH_MARGIN_SECONDS = 60;
const defaultFunctionsFactory = () => require("../../tools/conf").initFunctions();

const validCredentials = (value) => (
  value &&
  typeof value.apiKey === "string" && value.apiKey &&
  typeof value.collection === "string" && value.collection &&
  typeof value.endpoint === "string" && /^https?:\/\//.test(value.endpoint) &&
  Number.isFinite(value.expiresAt)
);

export const createTypesenseEntitySearchAdapter = ({
  fetchImplementation = (...args) => fetch(...args),
  functionsFactory = defaultFunctionsFactory,
  now = () => Date.now(),
} = {}) => {
  const credentialsByZoo = new Map();

  const credentialsFor = async (zooId, credentialScope, forceRefresh = false) => {
    const cached = credentialsByZoo.get(zooId);
    const nowSeconds = Math.floor(now() / 1000);
    if (
      !forceRefresh &&
      validCredentials(cached) && cached.credentialScope === credentialScope &&
      cached.expiresAt > nowSeconds + CREDENTIAL_REFRESH_MARGIN_SECONDS
    ) {
      return cached;
    }

    const callable = functionsFactory().httpsCallable("getUniversalSearchKeyV2");
    const response = await callable({ zooId });
    const credentials = response?.data;
    if (!validCredentials(credentials)) {
      throw new Error("Invalid universal search credentials");
    }
    credentialsByZoo.set(zooId, { ...credentials, credentialScope });
    return credentials;
  };

  const performSearch = async ({ query, zooId, entityTypes, userId, signal, forceRefresh = false }) => {
    const credentialScope = JSON.stringify([userId || null, [...entityTypes].sort()]);
    const credentials = await credentialsFor(zooId, credentialScope, forceRefresh);
    const params = new URLSearchParams({
      q: query,
      query_by: "title,aliases,subtitle,enclosure_name,sector_name",
      query_by_weights: "10,8,5,3,3",
      num_typos: "2,2,2,1,1",
      prefix: "true,true,true,true,true",
      prioritize_exact_match: "true",
      per_page: "30",
      sort_by: "_text_match:desc,sort_priority:desc,updated_at:desc",
      ...(entityTypes.length
        ? { filter_by: `entity_type:=[${entityTypes.join(",")}]` }
        : {}),
    });
    const endpoint = credentials.endpoint;
    const response = await fetchImplementation(
      `${endpoint}/collections/${encodeURIComponent(credentials.collection)}/documents/search?${params}`,
      {
        headers: {
          "X-TYPESENSE-API-KEY": credentials.apiKey,
          "Content-Type": "application/json",
        },
        signal,
      }
    );

    if ((response.status === 401 || response.status === 403) && !forceRefresh) {
      credentialsByZoo.delete(zooId);
      return performSearch({ query, zooId, entityTypes, userId, signal, forceRefresh: true });
    }
    if (!response.ok) {
      throw new Error(`Universal search failed (${response.status})`);
    }
    const payload = await response.json();
    return Array.isArray(payload?.hits) ? payload.hits : [];
  };

  return Object.freeze({
    clearCredentials(zooId) {
      if (zooId) credentialsByZoo.delete(zooId);
      else credentialsByZoo.clear();
    },
    search({ query, zooId, entityTypes = [], userId, signal }) {
      return performSearch({ query, zooId, entityTypes, userId, signal });
    },
  });
};

export default createTypesenseEntitySearchAdapter;
